import { CopilotRoleEnum } from "~/utils/enums/copilot/chat.enums";
import { hasVisibleContentOf, shouldRenderMessageOf } from "~/utils/helpers/copilot/message-parts";
import type { ICopilotUIMessage } from "~/utils/interfaces/copilot-interfaces";

/**
 * What the conversation paints, and where. Kept out of `CopilotConversation` because every rule
 * here is a decision about a list of messages — checkable on its own, unlike a rendered template.
 * `busy` is the chat's status: the stream is open, so an unfinished answer is expected.
 */

/** Keeps a stable placeholder until the stream carries something the panel can actually paint. */
export function isWaitingForVisibleContent(messages: ICopilotUIMessage[], busy: boolean): boolean {
  if (!busy) return false;

  const last = messages.at(-1);
  if (!last || last.role !== CopilotRoleEnum.ASSISTANT) return true;

  return !hasVisibleContentOf(last);
}

/** The answer at `index` is the one still waiting for its first paintable chunk. */
export function isWaitingMessageAt(messages: ICopilotUIMessage[], index: number, busy: boolean): boolean {
  const message = messages[index];

  return (
    !!message &&
    message.role === CopilotRoleEnum.ASSISTANT &&
    index === messages.length - 1 &&
    isWaitingForVisibleContent(messages, busy)
  );
}

export function shouldRenderMessageAt(messages: ICopilotUIMessage[], index: number, busy: boolean): boolean {
  const message = messages[index];
  if (!message) return false;

  return shouldRenderMessageOf(message, isWaitingMessageAt(messages, index, busy));
}

/** Nothing in the list can carry the sparkle, so the conversation adds a bubble for it. */
export function needsStandaloneLoader(messages: ICopilotUIMessage[], busy: boolean): boolean {
  if (!isWaitingForVisibleContent(messages, busy)) return false;

  return messages.at(-1)?.role !== CopilotRoleEnum.ASSISTANT;
}

/**
 * The answer the `extras` slot attaches to: the last one that paints something of its own, or -1
 * when none does. A waiting placeholder is skipped — it renders a sparkle instead of its content,
 * so a card hosted there would disappear for as long as the stream takes.
 *
 * The search stops at the admin's last message, and never hosts an answer older than it. The extras
 * describe the run as it stands now, so hosting them further up repaints an exchange the admin has
 * already replied to: correcting a session match ("la bonne session est le 12/05") swapped the
 * candidate list *above* the correction, seconds before the new answer arrived below it.
 *
 * `ownerMessageId` names the answer that actually produced what the slot shows. Given one, the last
 * answer hosts the extras only if it is that answer — a reply that produced nothing of its own must
 * not inherit them. Left null, any answer may host: the caller's card belongs to no particular turn.
 */
export function extrasHostIndexOf(
  messages: ICopilotUIMessage[],
  busy: boolean,
  ownerMessageId: string | null = null,
): number {
  if (extrasAreStale(messages, ownerMessageId)) return -1;

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index]?.role === CopilotRoleEnum.USER) return -1;
    if (messages[index]?.role !== CopilotRoleEnum.ASSISTANT) continue;
    if (!shouldRenderMessageAt(messages, index, busy) || isWaitingMessageAt(messages, index, busy)) continue;

    return !ownerMessageId || messages[index]?.id === ownerMessageId ? index : -1;
  }

  return -1;
}

/**
 * The admin has spoken since the answer that produced the extras. They then describe an exchange
 * already replied to: asked to name the session again ("Merci de préciser les critères …"), the
 * assistant's question inherited the picker the admin had just disavowed — ticked card included —
 * and its "Ouvrir les présences pré-remplies".
 *
 * Told apart from an owner that simply has no bubble: an answer carrying only a tool result paints
 * nothing at all — the backend suppresses the prose that would duplicate it — so it cannot host
 * anything, and its extras belong in a bubble of their own. Reading "no host" as "stale" is what
 * made a corrected session choice arrive invisible, and reappear on reload.
 */
export function extrasAreStale(messages: ICopilotUIMessage[], ownerMessageId: string | null = null): boolean {
  if (!ownerMessageId) return false;

  const ownerIndex = messages.findIndex((message) => message.id === ownerMessageId);
  if (ownerIndex === -1) return false;

  return messages.slice(ownerIndex + 1).some((message) => message.role === CopilotRoleEnum.USER);
}

/**
 * Nothing in the conversation can host the extras, so they get a bubble of their own — unless the
 * stream is open, because then the answer being written is where they belong. Standing them alone
 * mid-stream would put them under the sparkle and move them again the moment the answer paints.
 *
 * Extras the admin has already replied past get no bubble either: they are not homeless, they are
 * history, and standing them alone would tear them off the turn that made them.
 */
export function needsStandaloneExtras(
  hasExtras: boolean,
  extrasHostIndex: number,
  busy: boolean,
  areStale = false,
): boolean {
  return hasExtras && extrasHostIndex === -1 && !busy && !areStale;
}

/** The thumbs only show on a finished answer that actually carries content. */
export function canRateMessageAt(messages: ICopilotUIMessage[], index: number, busy: boolean): boolean {
  const message = messages[index];
  if (!message || message.role !== CopilotRoleEnum.ASSISTANT || !hasVisibleContentOf(message)) return false;

  return !busy || index < messages.length - 1;
}
